import AppDialog from '@components/ui/AppDialog';
import ProgressBar from '@components/ProgressBar';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CancelIcon,
  Caption,
  CheckCircleIcon,
  Flex,
  Spinner,
  Title,
  theme,
} from '@kvdbil/components';
import { responsivePadding } from '@utils/styles';
import { SuccessImage, UploadResult } from '@shared/types';
import UploadFeedback from './components/UploadFeedback';
import { formatRegistrationPlate, getVehicleFullName } from '@utils/vehicle.ts';
import { useSelector } from 'react-redux';
import { selectItemById } from '@data/selectors/itemSelectors.ts';
import { useParams } from 'react-router-dom';

const Container = styled.div`
  overflow: initial;
  display: flex;
  flex-direction: column;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.125rem;
`;

const GridItem = styled.div`
  background-color: ${({ theme }) => theme.colors.background.gray};
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  &:before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`;

const GridItemFeedback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: end;
  align-items: end;
  padding: 0.25rem;
`;

const GridItemIconBackground = styled(Flex)`
  background: ${({ theme }) => theme.colors.background.light};
  border-radius: 50%;
  padding: 0.25rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${responsivePadding}
  padding-top: 1rem;
  padding-bottom: 0.5rem;
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
`;

const SubTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 52px;
  background: ${theme.colors.background.light};
  padding: 0 1rem;
`;

const SubTitle = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${theme.colors.text.dark};
  font-family: ${theme.typography.fontAlternativeFamily};
  font-size: 1.125rem;
  font-weight: 400;
`;

type UploadViewProps = {
  fileUrls: string[];
  uploadedImages: UploadResult[];
  onCancelUpload: () => void;
  dialogOpen: boolean;
  onCLoseDialog: () => void;
  onOpenDialog: () => void;
};

const UploadView = ({
  fileUrls,
  uploadedImages,
  onCancelUpload,
  dialogOpen,
  onCLoseDialog,
  onOpenDialog,
}: UploadViewProps) => {
  const { t } = useTranslation();
  const { itemId } = useParams<{
    itemId: string;
  }>();
  const item = useSelector(selectItemById(itemId));
  const successImages = uploadedImages.filter(
    image => image.status === 'SUCCESS'
  ) as SuccessImage[];

  const progress = uploadedImages.length;
  const success = successImages.length;
  const max = fileUrls?.length || 0;

  const formattedRegNr = formatRegistrationPlate(
    item?.properties?.registrationPlate ?? ''
  );

  const subTitle = item ? getVehicleFullName(item) : itemId;
  const title = `${item?.properties?.registrationPlate ? formattedRegNr + ' | ' : ''}${subTitle}`;

  return (
    <Container>
      <SubTitleWrapper>
        <SubTitle>{title}</SubTitle>
      </SubTitleWrapper>
      <HeaderContainer>
        <Flex grow={1}>
          <Flex grow={1} direction={'column'} gap={0.1}>
            <Flex grow={0}>
              <Caption>
                {t('Uploaded {{progress}} av {{max}}', { progress, max })}
              </Caption>
            </Flex>
            <ProgressBar
              progress={progress}
              max={max}
              color={theme.colors.primary.main}
            />
          </Flex>

          <Flex shrink={1} grow={0}>
            {progress === max && (
              <CheckCircleIcon
                width={'2rem'}
                height={'2rem'}
                color={theme.colors.secondary.main}
                onClick={onOpenDialog}
              />
            )}
            {progress !== max && (
              <Button variant="flat" color="neutral" onClick={onCancelUpload}>
                {t('Cancel')}
              </Button>
            )}
          </Flex>
        </Flex>
      </HeaderContainer>

      <Grid>
        {fileUrls.map((url, index) => {
          const image = uploadedImages?.[index];

          if (!image) {
            return (
              <GridItem key={index}>
                <StyledImg src={url} alt="" />

                <GridItemFeedback>
                  <GridItemIconBackground>
                    <Spinner size={'smaller'} />
                  </GridItemIconBackground>
                </GridItemFeedback>
              </GridItem>
            );
          }

          if (uploadedImages[index]?.status !== 'SUCCESS') {
            return (
              <GridItem key={index}>
                <StyledImg src={url} alt="" />
                <GridItemFeedback>
                  <GridItemIconBackground>
                    <CancelIcon
                      color={theme.colors.error.main}
                      width={'1.5rem'}
                      height={'1.5rem'}
                    />
                  </GridItemIconBackground>
                </GridItemFeedback>
              </GridItem>
            );
          }

          return (
            <GridItem key={index}>
              <StyledImg src={url} alt="" />
              <StyledImg src={(image as SuccessImage).image.cdnUri} alt="" />

              <GridItemFeedback>
                <GridItemIconBackground>
                  <CheckCircleIcon
                    color={theme.colors.secondary.main}
                    width={'1.5rem'}
                    height={'1.5rem'}
                  />
                </GridItemIconBackground>
              </GridItemFeedback>
            </GridItem>
          );
        })}
      </Grid>

      <AppDialog
        title={<Title>{t('Summary')}</Title>}
        isOpen={dialogOpen}
        actions={
          <>
            <Button onClick={onCLoseDialog} color="neutral" variant="flat">
              {t('Close')}
            </Button>
          </>
        }
        onClose={onCLoseDialog}
      >
        <UploadFeedback
          success={success}
          max={max}
          uploadedImages={uploadedImages}
        />
      </AppDialog>
    </Container>
  );
};

export default UploadView;
